import { useMemo, useState } from 'react';

interface PagarModalProps {
  onClose: () => void;
  monto: number;
  mes: string;
  folio: string;
  planNombre?: string;
  onPaymentPrepared?: (payload: {
    metodo: 'tarjeta' | 'tienda';
    referencia?: string;
    domiciliado?: boolean;
    cardLast4?: string;
    amount: number;
    period: string;
  }) => Promise<void> | void;
}

type PaymentStep = 'menu' | 'tarjeta' | 'tienda' | 'confirmacion';

const formatCard = (value: string) => {
  const numbers = value.replace(/\D/g, '').slice(0, 16);
  return numbers.replace(/(.{4})/g, '$1 ').trim();
};

const formatExpiry = (value: string) => {
  const numbers = value.replace(/\D/g, '').slice(0, 4);
  if (numbers.length >= 2) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  return numbers;
};

function buildReference(folio: string) {
  const base = folio.replace(/\D/g, '').slice(-6).padStart(6, '0');
  const suffix = String(Date.now()).slice(-8);
  return `92${base}${suffix}`.slice(0, 16);
}

export default function PagarModal({ onClose, monto, mes, folio, planNombre = 'Coverly', onPaymentPrepared }: PagarModalProps) {
  const [step, setStep] = useState<PaymentStep>('menu');
  const [paymentResult, setPaymentResult] = useState<'tarjeta' | 'tienda'>('tarjeta');
  const [referenceCopied, setReferenceCopied] = useState(false);
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '' });
  const [domiciliado, setDomiciliado] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const reference = useMemo(() => buildReference(folio), [folio]);

  const validateCard = () => {
    const nextErrors: Record<string, string> = {};
    if (card.number.replace(/\s/g, '').length < 16) nextErrors.number = 'Ingresa una tarjeta valida';
    if (!card.name.trim()) nextErrors.name = 'Ingresa el nombre del titular';
    if (card.expiry.length < 5) nextErrors.expiry = 'Fecha invalida';
    if (card.cvv.length < 3) nextErrors.cvv = 'CVV invalido';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const confirmCard = async () => {
    if (!validateCard()) return;
    await onPaymentPrepared?.({
      metodo: 'tarjeta',
      domiciliado,
      cardLast4: card.number.replace(/\s/g, '').slice(-4),
      amount: monto,
      period: mes,
    });
    setPaymentResult('tarjeta');
    setStep('confirmacion');
  };

  const confirmStoreReference = async () => {
    await onPaymentPrepared?.({
      metodo: 'tienda',
      referencia: reference,
      amount: monto,
      period: mes,
    });
    setPaymentResult('tienda');
    setStep('confirmacion');
  };

  const copyReference = async () => {
    await navigator.clipboard.writeText(reference).catch(() => {});
    setReferenceCopied(true);
    setTimeout(() => setReferenceCopied(false), 1800);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0d0520]/60 px-4 py-6 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-[30px] bg-white px-5 pb-6 pt-5 shadow-2xl">
        <div className="mb-4 flex justify-center">
          <div className="h-1 w-11 rounded-full bg-[#E3E1EA]" />
        </div>

        <header className="mb-4 flex items-center justify-between gap-3">
          {step !== 'menu' ? (
            <button
              type="button"
              onClick={() => setStep('menu')}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F4FA] text-[#534AB7]"
            >
              <i className="ri-arrow-left-line text-xl" />
            </button>
          ) : (
            <div className="h-9 w-9" />
          )}
          <div className="text-center">
            <p className="text-base font-black text-[#211B4F]">Pagar membresia</p>
            <p className="text-xs font-semibold text-[#7C7589]">Plan {planNombre} · {mes}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F4FA] text-[#534AB7]"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </header>

        <section className="mb-4 rounded-3xl border border-[#E3E0FA] bg-[#F7F6FF] p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-wide text-[#7C7589]">Monto a pagar</p>
              <p className="mt-1 text-3xl font-black text-[#534AB7]">${monto}</p>
              <p className="text-xs font-semibold text-[#7C7589]">MXN mensual</p>
            </div>
            <div className="rounded-2xl bg-white px-3 py-2 text-right">
              <p className="text-[11px] font-bold text-[#7C7589]">Folio</p>
              <p className="font-mono text-xs font-black text-[#211B4F]">{folio || 'CVR-PENDIENTE'}</p>
            </div>
          </div>
        </section>

        {step === 'menu' && (
          <div className="grid gap-3">
            <p className="text-sm leading-6 text-[#66636D]">
              Elige como quieres cubrir la mensualidad de tu membresia. Puedes pagar con tarjeta o generar una referencia para tienda de autoservicio.
            </p>

            <button
              type="button"
              onClick={() => setStep('tarjeta')}
              className="flex w-full items-center gap-4 rounded-3xl border border-[#E3E0FA] bg-white p-4 text-left shadow-sm active:scale-[0.99]"
            >
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#EFEDFF] text-[#534AB7]">
                <i className="ri-bank-card-line text-2xl" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-black text-[#211B4F]">Pagar con tarjeta</span>
                <span className="mt-1 block text-xs leading-5 text-[#7C7589]">Debito, credito y opcion de pago domiciliado.</span>
              </span>
              <i className="ri-arrow-right-s-line text-2xl text-[#C6C1E8]" />
            </button>

            <button
              type="button"
              onClick={() => setStep('tienda')}
              className="flex w-full items-center gap-4 rounded-3xl border border-[#D8EEE6] bg-white p-4 text-left shadow-sm active:scale-[0.99]"
            >
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#EAF8F2] text-[#1D9E75]">
                <i className="ri-barcode-line text-2xl" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-black text-[#211B4F]">Generar referencia</span>
                <span className="mt-1 block text-xs leading-5 text-[#7C7589]">Paga en OXXO, 7-Eleven, Farmacias del Ahorro y mas.</span>
              </span>
              <i className="ri-arrow-right-s-line text-2xl text-[#B7DCCF]" />
            </button>
          </div>
        )}

        {step === 'tarjeta' && (
          <div className="grid gap-3">
            <div className="rounded-3xl bg-[#F7F6FF] p-4">
              <p className="text-sm font-black text-[#211B4F]">Datos de tarjeta</p>
              <p className="mt-1 text-xs leading-5 text-[#7C7589]">
                Esta pantalla deja preparado el flujo visual. La validacion bancaria real se conecta despues con el procesador de pagos.
              </p>
            </div>

            <InputBox error={errors.number}>
              <i className="ri-bank-card-line text-[#7C7589]" />
              <input
                type="text"
                inputMode="numeric"
                value={card.number}
                onChange={(event) => setCard({ ...card, number: formatCard(event.target.value) })}
                placeholder="Numero de tarjeta"
                className="min-w-0 flex-1 bg-transparent py-3 text-sm outline-none"
              />
            </InputBox>
            {errors.number && <p className="-mt-2 text-xs text-red-500">{errors.number}</p>}

            <InputBox error={errors.name}>
              <i className="ri-user-line text-[#7C7589]" />
              <input
                type="text"
                value={card.name}
                onChange={(event) => setCard({ ...card, name: event.target.value.toUpperCase() })}
                placeholder="Nombre del titular"
                className="min-w-0 flex-1 bg-transparent py-3 text-sm uppercase outline-none"
              />
            </InputBox>
            {errors.name && <p className="-mt-2 text-xs text-red-500">{errors.name}</p>}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <InputBox error={errors.expiry}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={card.expiry}
                    onChange={(event) => setCard({ ...card, expiry: formatExpiry(event.target.value) })}
                    placeholder="MM/AA"
                    className="min-w-0 flex-1 bg-transparent py-3 text-sm outline-none"
                  />
                </InputBox>
              </div>
              <div>
                <InputBox error={errors.cvv}>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={card.cvv}
                    onChange={(event) => setCard({ ...card, cvv: event.target.value.replace(/\D/g, '') })}
                    placeholder="CVV"
                    className="min-w-0 flex-1 bg-transparent py-3 text-sm outline-none"
                  />
                </InputBox>
              </div>
            </div>
            {(errors.expiry || errors.cvv) && <p className="-mt-2 text-xs text-red-500">{errors.expiry || errors.cvv}</p>}

            <label className="flex items-start gap-3 rounded-2xl border border-[#E3E0FA] bg-[#F7F6FF] p-4">
              <button
                type="button"
                onClick={() => setDomiciliado(!domiciliado)}
                className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2"
                style={{ backgroundColor: domiciliado ? '#534AB7' : 'white', borderColor: domiciliado ? '#534AB7' : '#D1D5DB' }}
              >
                {domiciliado && <i className="ri-check-line text-xs text-white" />}
              </button>
              <span className="text-xs leading-5 text-[#625D6E]">
                Guardar esta tarjeta para cargo mensual domiciliado de mi membresia Coverly.
              </span>
            </label>

            <button
              type="button"
              onClick={confirmCard}
              className="mt-1 w-full rounded-2xl bg-[#534AB7] py-4 text-base font-black text-white shadow-lg shadow-[#534AB7]/20 active:scale-[0.98]"
            >
              Pagar ${monto} MXN
            </button>
          </div>
        )}

        {step === 'tienda' && (
          <div className="grid gap-3">
            <div className="rounded-3xl border border-[#D8EEE6] bg-[#F0FBF7] p-4 text-center">
              <p className="text-xs font-black uppercase tracking-wide text-[#177A5E]">Referencia de pago</p>
              <p className="mt-2 font-mono text-2xl font-black tracking-wider text-[#211B4F]">{reference}</p>
              <button
                type="button"
                onClick={copyReference}
                className="mt-3 rounded-full bg-white px-4 py-2 text-xs font-black text-[#177A5E]"
              >
                {referenceCopied ? 'Referencia copiada' : 'Copiar referencia'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {['OXXO', '7-Eleven', 'Farmacias', 'Walmart', 'Chedraui', 'Soriana'].map((store) => (
                <div key={store} className="rounded-2xl bg-[#F7F6FF] px-3 py-3 text-center text-xs font-black text-[#45405F]">
                  {store}
                </div>
              ))}
            </div>

            <p className="text-xs leading-5 text-[#7C7589]">
              Presenta esta referencia en caja y paga ${monto} MXN. Cuando el pago sea confirmado, tu membresia se actualizara en la app.
            </p>

            <button
              type="button"
              onClick={confirmStoreReference}
              className="w-full rounded-2xl bg-[#1D9E75] py-4 text-base font-black text-white shadow-lg shadow-[#1D9E75]/20 active:scale-[0.98]"
            >
              Listo, usare esta referencia
            </button>
          </div>
        )}

        {step === 'confirmacion' && (
          <div className="grid gap-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF8F2] text-[#1D9E75]">
              <i className="ri-checkbox-circle-line text-4xl" />
            </div>
            <div>
              <p className="text-xl font-black text-[#211B4F]">
                {paymentResult === 'tarjeta' ? 'Pago preparado' : 'Referencia generada'}
              </p>
              <p className="mt-2 text-sm leading-6 text-[#66636D]">
                {paymentResult === 'tarjeta'
                  ? `Se registro el intento de pago por $${monto} MXN del Plan ${planNombre}.`
                  : `Tu referencia para pagar $${monto} MXN en tienda quedo lista.`}
              </p>
            </div>
            <div className="rounded-2xl bg-[#F7F6FF] p-4 text-left">
              <p className="text-xs font-black uppercase tracking-wide text-[#7C7589]">Resumen</p>
              <div className="mt-3 grid gap-2 text-sm">
                <SummaryRow label="Plan" value={`Plan ${planNombre}`} />
                <SummaryRow label="Monto" value={`$${monto} MXN`} />
                <SummaryRow label="Folio" value={folio || 'CVR-PENDIENTE'} />
                {paymentResult === 'tienda' && <SummaryRow label="Referencia" value={reference} />}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-2xl bg-[#534AB7] py-4 text-base font-black text-white"
            >
              Entendido
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InputBox({ children, error }: { children: React.ReactNode; error?: string }) {
  return (
    <div
      className="flex items-center gap-3 rounded-2xl border bg-white px-4"
      style={{ borderColor: error ? '#E24B4A' : '#E3E0FA' }}
    >
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[#7C7589]">{label}</span>
      <span className="text-right font-black text-[#211B4F]">{value}</span>
    </div>
  );
}
